import { TextField as MuiTextField, type TextFieldProps } from '@mui/material';
import { mergeSlotProps, useForkRef } from '@mui/material/utils';
import { type Control, type FieldPath, type FieldValues, useController } from 'react-hook-form';

export type MuiTextFieldProps = TextFieldProps & { 'data-test'?: string };

export type RHFTextFieldProps<T extends FieldValues> = Omit<MuiTextFieldProps, 'name'> & {
  name: FieldPath<T>;
  control?: Control<T>;
  rules?: Record<string, unknown>;
};

export const RHFTextField = <T extends Record<string, unknown> = Record<string, string>>({
  name,
  control,
  rules,
  slotProps,
  ...rest
}: RHFTextFieldProps<T>): JSX.Element => {
  const {
    field: { ref: fieldRef, ...field },
    fieldState: { invalid, error },
    formState: { isSubmitting },
  } = useController({
    control,
    name,
    rules: rules,
  });
  // RHF focuses invalid fields through this ref, so it has to point at the <input>, not the root <div>
  const inputRef = useForkRef(fieldRef, rest.inputRef);

  const defaultProps: MuiTextFieldProps = {
    'data-test': rest['data-test'],
    variant: 'standard',
  };

  return (
    <MuiTextField
      {...defaultProps}
      {...field}
      {...rest}
      inputRef={inputRef}
      slotProps={{
        ...slotProps,
        inputLabel: mergeSlotProps(slotProps?.inputLabel, { disableAnimation: true, shrink: true }),
        input: mergeSlotProps(slotProps?.input, { disableUnderline: true }),
      }}
      disabled={isSubmitting || rest.disabled}
      error={invalid || rest.error}
      helperText={error?.message || rest.helperText}
    />
  );
};
